import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { PRICING_PLANS } from '@/constants/pricing';

declare global {
  interface Window {
    Razorpay: any;
  }
}

export const useRazorpay = () => {
  const { user, updateUser } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);

  const createSubscription = async (plan: 'starter' | 'pro' | 'team') => {
    if (!user?.id) {
      toast.error('Please log in to subscribe to Genova AI Studio');
      return;
    }

    setIsProcessing(true);
    console.log('Starting subscription creation for plan:', plan);

    try {
      // Show loading toast
      toast.loading('Creating subscription...', { id: 'create-subscription' });
      
      const { data, error } = await supabase.functions.invoke('create-subscription', {
        body: { 
          plan, 
          userId: user.id,
          userEmail: user.email,
          userName: user.name || user.email
        }
      });

      console.log('Subscription response:', { data, error });

      if (error) {
        console.error('Subscription invocation error:', error);
        toast.dismiss('create-subscription');
        toast.error(`Subscription failed: ${error.message}`);
        setIsProcessing(false);
        return;
      }

      if (!data) {
        console.error('No data received from subscription function');
        toast.dismiss('create-subscription');
        toast.error('No response from payment service. Please try again.');
        setIsProcessing(false);
        return;
      }

      if (!data.success) {
        console.error('Subscription creation failed:', data);
        toast.dismiss('create-subscription');
        
        let errorMessage = 'Failed to create subscription. Please try again.';
        
        // Handle specific error codes
        switch (data.code) {
          case 'MISSING_RAZORPAY_CREDENTIALS':
            errorMessage = 'Payment service is not configured. Please contact support.';
            break;
          case 'INVALID_REQUEST_FORMAT':
            errorMessage = 'Invalid request format. Please try again.';
            break;
          case 'MISSING_REQUIRED_FIELDS':
            errorMessage = 'Missing required information. Please try again.';
            break;
          case 'INVALID_PLAN_SELECTION':
            errorMessage = `Invalid plan selected: ${plan}`;
            break;
          case 'PAYMENT_GATEWAY_CONNECTION_ERROR':
            errorMessage = 'Unable to connect to payment service. Please try again later.';
            break;
          case 'PAYMENT_ORDER_CREATION_FAILED':
            errorMessage = 'Payment order creation failed. Please try again.';
            break;
          default:
            errorMessage = data.error || 'Failed to create subscription. Please try again.';
        }
        
        toast.error(errorMessage);
        setIsProcessing(false);
        return;
      }

      toast.dismiss('create-subscription');
      console.log('Subscription created successfully, loading Razorpay...');

      // Load Razorpay script if not already loaded
      if (!window.Razorpay) {
        try {
          toast.loading('Loading payment gateway...', { id: 'load-razorpay' });
          
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.async = true;
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
          });
          
          toast.dismiss('load-razorpay');
        } catch (scriptError) {
          console.error('Failed to load Razorpay script:', scriptError);
          toast.dismiss('load-razorpay');
          toast.error('Failed to load payment gateway. Please refresh and try again.');
          setIsProcessing(false);
          return;
        }
      }

      // Get plan limits from PRICING_PLANS - these must match the edge function
      const planLimits = {
        starter: PRICING_PLANS.starter.limits,
        pro: PRICING_PLANS.pro.limits,
        team: PRICING_PLANS.business.limits // business is the same as team
      };

      const currentPlanLimits = planLimits[plan];
      const currentPlanConfig = plan === 'team' ? PRICING_PLANS.business : PRICING_PLANS[plan];

      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: 'USD',
        name: 'Genova AI Studio',
        description: data.description || `${currentPlanConfig.name} Plan - ${currentPlanConfig.price}/month`,
        order_id: data.orderId,
        prefill: {
          name: user.name || user.email,
          email: user.email,
        },
        theme: {
          color: '#8B5CF6',
        },
        handler: async (response: any) => {
          try {
            console.log('Payment completed:', response);
            toast.loading('Verifying payment...', { id: 'verify-payment' });
            
            const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-payment', {
              body: { 
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                signature: response.razorpay_signature
              }
            });

            console.log('Payment verification response:', { verifyData, verifyError });

            if (verifyError) {
              console.error('Payment verification invocation error:', verifyError);
              toast.dismiss('verify-payment');
              toast.error('Payment verification failed. Please contact support.');
              setIsProcessing(false);
              return;
            }

            if (!verifyData || !verifyData.success) {
              console.error('Payment verification failed:', verifyData);
              toast.dismiss('verify-payment');
              
              let errorMsg = 'Payment verification failed. Please contact support.';
              
              // Handle specific error codes
              if (verifyData?.code) {
                switch (verifyData.code) {
                  case 'MISSING_ENV_VARS':
                    errorMsg = 'Service configuration error. Please contact support.';
                    break;
                  case 'SIGNATURE_MISMATCH':
                    errorMsg = 'Payment verification failed. Please contact support if money was deducted.';
                    break;
                  default:
                    errorMsg = verifyData.error || 'Payment verification failed. Please contact support.';
                }
              }
              
              toast.error(errorMsg);
              setIsProcessing(false);
              return;
            }

            // Update local user state with new plan
            updateUser({ 
              plan: plan
            });

            toast.dismiss('verify-payment');
            toast.success(`🎉 Welcome to Genova AI Studio ${currentPlanConfig.name}! You now have ${currentPlanLimits.images} images, ${currentPlanLimits.scripts} scripts, and ${currentPlanLimits.audioMinutes} minutes of audio per month!`, {
              duration: 5000
            });
            
            setIsProcessing(false);
            
            // Redirect to dashboard after successful payment
            setTimeout(() => {
              window.location.href = '/dashboard';
            }, 2000);
            
          } catch (error) {
            console.error('Payment processing error:', error);
            toast.dismiss('verify-payment');
            toast.error('Payment processing failed. Please contact support.');
            setIsProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            console.log('Payment modal dismissed');
            toast.info('Payment cancelled');
            setIsProcessing(false);
          }
        }
      };

      console.log('Opening Razorpay with options:', { ...options, key: options.key ? options.key.substring(0, 8) + '...' : 'undefined' });
      
      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error: any) {
      console.error('Subscription error:', error);
      toast.error(`Failed to create subscription: ${error.message}`);
      setIsProcessing(false);
    }
  };

  return {
    createSubscription,
    isProcessing
  };
};
